import type { CurrentSession } from '@elite-residence/api-client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useApiClient } from '../api/ApiClientProvider';
import { type Dictionary, useText } from '../i18n/LocaleProvider';
import { QueryFailure, readResponse } from '../ui/ui';

// The signed-in Staff member (GET /api/v1/auth/session). The server decides
// everything: this only reflects the session for navigation. Permission keys
// hide unavailable actions; the server still rechecks each one (BR-PERM-005).

export const SESSION_KEY = ['auth', 'session'] as const;

export function useSession() {
  const client = useApiClient();
  return useQuery({
    queryKey: SESSION_KEY,
    queryFn: async ({ signal }) => {
      const response = await client.getCurrentSession({ signal });
      if (response.status === 401) return null;
      return readResponse(response);
    },
    staleTime: 30_000,
  });
}

export function useHasPermission(): (key: string) => boolean {
  const session = useSession();
  return (key) => session.data?.permissions.includes(key) ?? false;
}

export function useSignedOut(): () => void {
  const queries = useQueryClient();
  return () => {
    queries.setQueryData(SESSION_KEY, null);
    queries.removeQueries({ predicate: (query) => query.queryKey[0] !== 'auth' });
  };
}

const text: Dictionary<'loading' | 'unavailable'> = {
  en: { loading: 'Loading…', unavailable: 'The service could not be reached.' },
  ar: { loading: 'جارٍ التحميل…', unavailable: 'تعذّر الوصول إلى الخدمة.' },
};

// Protected pages: a signed-out (or expired) visitor goes to the login page,
// which returns them here afterwards.
export function RequireSession({ children }: { readonly children: (session: CurrentSession) => ReactNode }) {
  const session = useSession();
  const location = useLocation();
  const t = useText(text);
  if (session.isPending) return <p role="status">{t.loading}</p>;
  if (session.isError) return <QueryFailure query={session} />;
  if (!session.data) {
    const next = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
  }
  return <>{children(session.data)}</>;
}

// Only same-application paths are accepted as a post-login destination.
export function safeNext(value: string | null): string {
  return value && /^\/(?!\/)[A-Za-z0-9/_?=&%.-]*$/.test(value) ? value : '/';
}

// One-time tokens arrive in the URL FRAGMENT (#token=...), which is never
// sent to a server; it is cleared from the address bar once read.
export function tokenFromFragment(hash: string): string | null {
  const match = /(?:^#|&)token=([A-Za-z0-9_-]{43,512})(?:&|$)/.exec(hash);
  return match?.[1] ?? null;
}
